import { Component, Input, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FieldOption, GetRegistrationApiModel } from '@public/src/api/generated/models';
import { AuthService } from '@public/src/application/services/auth/auth.service';
import { LicenceHolderService } from '@services/licence_holder.service';
import { sharedResolution } from '../../../src/shared_resolution';
import { SpaContext } from '../Classes/spacontext';
import { GlobalValidator } from '../Common/Classes/globalValidator';
import { TranslateService } from '../Common/Services/translate.service';
import { RegisterClientModel, RegistrationService } from '../Services/api/v2/registration.service';
import { UnitService } from '../Services/api/v2/Units/units.service';
import { UsageAgreementsService } from '../Services/api/v2/usageAgreements.service';
import { AuthContextService } from '../Services/authContext.service';
import { ModalService } from '../Services/modalService';
import { SpaEventService } from '../Services/spaEvents.service';
import { ConfirmPhoneNumberComponent } from './confirmPhoneNumber';

@Component({
	standalone: false,
	selector: 'visiba-registration',
	template: `
		<div class="regContainer">
			<div class="overlay" (click)="abort()"></div>
			<div class="container">
				<div class="row">
					<div class="inner span26 offset25 lspan26 loffset17" *ngIf="formCG !== null">
						<form
							novalidate
							(ngSubmit)="onSubmit()"
							[formGroup]="formCG"
							*ngIf="registrationData != null"
							[class.ng-submitted]="formCG['submitted']"
						>
							<div class="row icon">
								<visiba-svg a="AvatarCircle"></visiba-svg>
							</div>
							<div
								class="row intro span22 offset2"
								[innerHtml]="'LicenceHolder.Component.RegistrationComponent.Phrase.Body' | translate"
							></div>
							<div class="row"></div>
							<div class="registration-fields row">
								<div class="span10 offset2">
									<visiba-input
										type="text"
										formControlName="FirstName"
										tplaceholder="Common.Placeholder.GivenName"
										placeholder="Common.Placeholder.GivenName"
									>
									</visiba-input>
								</div>
								<div class="span10 offset2">
									<visiba-input
										type="text"
										formControlName="LastName"
										tplaceholder="Common.Placeholder.Surname"
										placeholder="Common.Placeholder.Surname"
									>
									</visiba-input>
								</div>
								<div class="span10 offset2">
									<visiba-input
										type="email"
										formControlName="Email"
										tplaceholder="Common.Placeholder.Email"
										placeholder="Common.Placeholder.Email"
									>
									</visiba-input>
								</div>
								<div class="span10 offset2">
									<visiba-tel
										[readonly]="!canEditPhoneNumber()"
										formControlName="Phone"
										placeholder="Common.Placeholder.Phone"
									></visiba-tel>
								</div>
								<div class="span10 offset2" *ngIf="registrationData.NationalIdentityNumberRequirement !== 'Optional'">
									<visiba-nin
										type="text"
										[readonly]="!canEditNationalNumber()"
										formControlName="NationalIdentityNumber"
										[availibleCountries]="[registrationData.Nationality]"
										[placeholder]="registrationData.Nationality === 'GB' ? 'Common.Placeholder.NhsNumber' : null"
									>
									</visiba-nin>
								</div>
							</div>
							<div class="row">
								<button class="btn span10 offset8">
									{{ 'LicenceHolder.Component.RegistrationComponent.Button.CompleteRegistration' | translate }}
								</button>
							</div>
							<div class="row error">
								<div class="span22 offset2 center-text">
									{{ errorMessage }}
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	`,
	styleUrls: ['../Css/Components/registration.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class RegistrationComponent {
	@Input('target') returnUrl: string = null;
	@Input('ticket') registrationTicket: string = null;
	formCG: UntypedFormGroup = null;
	registrationData: GetRegistrationApiModel = null;
	errorMessage: string = null;

	private readonly authService = sharedResolution.resolve(AuthService);

	constructor(
		private _router: Router,
		private _spaContext: SpaContext,
		private _registrationService: RegistrationService,
		private spaEventService: SpaEventService,
		private _builder: UntypedFormBuilder,
		private modalService: ModalService,
		private unitService: UnitService,
		private translateService: TranslateService,
		private authContextService: AuthContextService,
		private _usageAgreementsService: UsageAgreementsService,
	) {}

	ngOnInit(): void {
		this._registrationService.getRegistration(this.registrationTicket).subscribe(
			(response) => {
				this.registrationData = response.data;

				this.formCG = this._builder.group({
					NationalIdentityNumber: new UntypedFormControl(
						{ nin: response.data.NationalIdentityNumber, countryCode: this.registrationData.Nationality },
						Validators.compose([
							GlobalValidator.conditionalRequiredNin([
								this.registrationData.NationalIdentityNumberRequirement === FieldOption.Required,
							]),
							GlobalValidator.nationalIdentityFormat,
						]),
					),
					Email: new UntypedFormControl(
						response.data.Email,
						Validators.compose([
							GlobalValidator.conditionalRequired([this.registrationData.EmailRequirement === FieldOption.Required]),
							GlobalValidator.mailFormat,
						]),
					),
					Phone: new UntypedFormControl(
						response.data.Phone,
						Validators.compose([
							GlobalValidator.conditionalRequired([this.registrationData.PhoneRequirement === FieldOption.Required]),
							GlobalValidator.mobilePhoneFormat,
						]),
					),
					FirstName: new UntypedFormControl(response.data.FirstName, Validators.compose([Validators.required])),
					LastName: new UntypedFormControl(response.data.LastName, Validators.compose([Validators.required])),
					CountryCode: new UntypedFormControl(response.data.Nationality, Validators.compose([Validators.required])),
					LanguageCode: new UntypedFormControl(µ.Env.LangCode, Validators.compose([Validators.required])),
				});
			},
			// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
			() => {},
		);
	}

	canEditNationalNumber(): boolean {
		if (
			this.registrationData.NationalIdentityNumber !== null &&
			this.registrationData.NationalIdentityNumber !== '' &&
			this.registrationData.NationalIdentityNumberRequirement === FieldOption.ReadOnly
		) {
			return false;
		} else {
			return true;
		}
	}

	canEditPhoneNumber(): boolean {
		if (
			this.registrationData.Phone !== null &&
			this.registrationData.Phone !== '' &&
			this.registrationData.PhoneRequirement === FieldOption.ReadOnly
		) {
			return false;
		} else {
			return true;
		}
	}

	onSubmit(): void {
		this.formCG['submitted'] = true;
		if (!this.formCG.valid) {
			return;
		}

		const sendModel: RegisterClientModel = {
			Nationality: this.formCG.value.CountryCode,
			NationalIdentityNumber: this.formCG.value.NationalIdentityNumber.nin,
			Phone: this.formCG.value.Phone,
			FirstName: this.formCG.value.FirstName,
			LastName: this.formCG.value.LastName,
			Email: this.formCG.value.Email,
		};

		const countryInfo = this._spaContext.unitConfig.Locale.SupportedCountries.find(
			(country) => country.CountryCode === this.formCG.value.CountryCode,
		);
		const phoneNumberToCheck = countryInfo.ClientIdentifierPhoneNumber && this.canEditPhoneNumber() ? sendModel.Phone : null;
		const nationalIdentityNumberToCheck =
			countryInfo.ClientIdentifierNationalIdentityNumber && this.canEditNationalNumber() ? sendModel.NationalIdentityNumber : null;

		this.unitService
			.getIdentifierConflicts(this.formCG.value.CountryCode, nationalIdentityNumberToCheck, phoneNumberToCheck)
			.subscribe((response) => {
				if (response.data.IdentifiersAlreadyInUse && this.canEditNationalNumber()) {
					this.errorMessage = this.translateService.get(
						'LicenceHolder.Component.RegistrationComponent.Error.PersonalIdentityNumberTaken',
					);

					return;
				}

				if (response.data.IdentifiersAlreadyInUse && this.canEditPhoneNumber()) {
					this.errorMessage = this.translateService.get('LicenceHolder.Component.RegistrationComponent.Error.PhoneNumber');

					return;
				}

				if (this.canEditPhoneNumber() && this.formCG.get('Phone').dirty) {
					const confirmPhoneNumberComponent = this.modalService.attach(ConfirmPhoneNumberComponent);
					confirmPhoneNumberComponent.instance.phoneNumber = this.formCG.value.Phone;
					confirmPhoneNumberComponent.instance.countryCode = this.formCG.value.CountryCode;
					confirmPhoneNumberComponent.instance.languageCode = this.formCG.value.LanguageCode;
					confirmPhoneNumberComponent.instance.onNumberConfirmed.subscribe((verificationCode) => {
						this.modalService.detach();
						sendModel.PhoneVerificationAttemptId = verificationCode;
						this.registerPatient(sendModel);
					});

					return;
				}
				this.registerPatient(sendModel);
			});
	}

	private registerPatient(model: RegisterClientModel): void {
		this._registrationService.completeRegistration(this.registrationTicket, model).subscribe(
			() => {
				this.spaEventService.onNotificationComplianceNeeded.emit({ AllowEmail: true, AllowSms: true, UpToDate: false });
				const complianceSubscription = this.spaEventService.onNotificationComplianceCompleted.subscribe(() => {
					complianceSubscription.unsubscribe();
					this.checkDocumentSigning();
				});
			},
			(error) => {
				if (error.ErrorCode === 'RegistrationAlreadyCompleted') {
					this.spaEventService.onRegistrationAborted.emit();
					const licenceHolderService = sharedResolution.resolve(LicenceHolderService);
					this._router.navigate([licenceHolderService.alias]);
				}

				this.errorMessage =
					error.ErrorMessage || this.translateService.get('LicenceHolder.Component.RegistrationComponent.Error.Default');
			},
		);
	}

	checkDocumentSigning(): void {
		this._usageAgreementsService.getSignatureDocuments(this._spaContext.unitConfig.LicenceHolder.Id).subscribe(
			(response) => {
				if (!response.data.PrivacyPolicy.HasSigned || !response.data.TermsOfService.HasSigned) {
					this.spaEventService.onDocumentsSigningNeeded.emit(response.data);

					const signingSubscription = this.spaEventService.onDocumentsSigningCompleted.subscribe(() => {
						this.completeRegistration();
						signingSubscription.unsubscribe();
					});

					return;
				}

				this.completeRegistration();
			},
			// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
			() => {},
		);
	}

	completeRegistration(): void {
		this.authService
			.fetchActiveIdentity()
			.then(() => {
				this.authContextService.signInPatient(null, false);
				this.spaEventService.onVisitorRegistred.emit();

				this._router.navigateByUrl(this.returnUrl);
			})
			.catch(() => {
				window.location.href = '/' + this._spaContext.baseHref + '/';
			});
	}

	abort(): void {
		this.spaEventService.onRegistrationAborted.emit();
	}
}
